import apiClient from './apiClient';

export const evaluationsApi = {
  /**
   * Create a new evaluation request
   * @param {Object} options - Evaluation options
   * @param {string} options.type - 'events' | 'strategies' | 'both'
   * @param {string} options.mode - 'changedOnly' | 'all' | 'selected'
   * @param {Array} options.selectedItems - Items to evaluate (for mode='selected')
   * @returns {Promise<Object>} Created evaluation request
   */
  createEvaluation: async (options = {}) => {
    const body = {
      type: options.type || 'both',
      mode: options.mode || 'changedOnly',
      selectedItems: options.selectedItems || []
    };
    // Optional backfill depth. The backend (POST /evaluations) floors this and
    // forwards it to the evaluation worker as `overrideLookbackDays`, overriding
    // the default ~400-candle lookback so an Evaluate can backfill deeper history
    // (e.g. 730 for two years of cycle fires). Omitted → backend default.
    if (Number.isFinite(options.lookbackDays) && options.lookbackDays > 0) {
      body.lookbackDays = Math.floor(options.lookbackDays);
    }
    const response = await apiClient.post('/evaluations', body);
    return response.data;
  },

  /**
   * Get evaluation request by ID
   * @param {string} requestId - Evaluation request ID
   * @returns {Promise<Object>} Evaluation request
   */
  getEvaluation: async (requestId) => {
    const response = await apiClient.get(`/evaluations/${requestId}`);
    return response.data;
  },

  /**
   * Get evaluation summary (quota + dirty items + latest request)
   * @returns {Promise<Object>} Evaluation summary
   */
  getSummary: async () => {
    const response = await apiClient.get('/evaluations/summary');
    return response.data;
  },

  /**
   * Get dirty items that would be evaluated
   * @param {string} type - 'events' | 'strategies' | 'both'
   * @returns {Promise<Object>} Dirty items
   */
  getDirtyItems: async (type = 'both') => {
    const params = new URLSearchParams();
    if (type) {
      params.append('type', type);
    }
    const response = await apiClient.get(`/evaluations/dirty?${params.toString()}`);
    return response.data;
  },

  /**
   * Get evaluation history
   * @param {Object} options - Query options
   * @param {number} options.limit - Max items to return
   * @param {string} options.lastEvaluatedKey - Pagination cursor
   * @returns {Promise<Object>} Evaluation history with pagination
   */
  getEvaluations: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) {
      params.append('limit', options.limit);
    }
    if (options.lastEvaluatedKey) {
      params.append('lastEvaluatedKey', options.lastEvaluatedKey);
    }
    const response = await apiClient.get(`/evaluations?${params.toString()}`);
    return response.data;
  },

  /**
   * Poll evaluation status until completion
   * @param {string} requestId - Evaluation request ID
   * @param {Object} options - Polling options
   * @param {number} options.interval - Polling interval in ms (default 3000)
   * @param {number} options.timeout - Max time to poll in ms (default 300000 = 5 min)
   * @param {Function} options.onProgress - Callback for progress updates
   * @returns {Promise<Object>} Final evaluation state
   */
  pollUntilComplete: async (requestId, options = {}) => {
    const { interval = 3000, timeout = 300000, onProgress } = options;
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const evaluation = await evaluationsApi.getEvaluation(requestId);

      if (onProgress) {
        onProgress(evaluation);
      }

      if (evaluation.status === 'completed' || evaluation.status === 'failed') {
        return evaluation;
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, interval));
    }

    throw new Error('Evaluation polling timeout');
  }
};

export default evaluationsApi;
